import { computed, onBeforeUnmount, ref } from 'vue'

const DEFAULT_DEBOUNCE = 300

export default function useListSearch({
  search,
  searchFun,
  quickFields,
  defaultQuickField = '',
  createAdvancedFilterCondition,
  onQuickFieldChange,
  onOpenAdvanced,
  debounce = DEFAULT_DEBOUNCE,
  preserveSearchKeys = []
}) {
  const searchMode = ref('quick')
  const quickSearchField = ref(defaultQuickField)
  const quickSearchValue = ref('')
  const advancedDrawerVisible = ref(false)
  const advancedFilterSnapshot = ref(null)
  const filterStr = ref('')
  const quickSearchTimer = ref(null)

  const getQuickFields = () => {
    return quickFields?.value || quickFields || {}
  }

  const getAdvancedConfig = () => {
    return createAdvancedFilterCondition ? createAdvancedFilterCondition() : {}
  }

  const clearSearchObject = () => {
    Object.keys(search).forEach(key => {
      if (preserveSearchKeys.includes(key)) return
      search[key] = ''
    })
  }

  const patchSearchObject = (params = {}) => {
    clearSearchObject()
    Object.keys(params).forEach(key => {
      if (key in search) search[key] = params[key]
    })
  }

  const resetQuickSearchState = () => {
    quickSearchField.value = defaultQuickField
    quickSearchValue.value = ''
  }

  const resetAdvancedSearchState = () => {
    advancedFilterSnapshot.value = null
    filterStr.value = ''
  }

  const hasValue = (value) => {
    if (Array.isArray(value)) return value.length > 0
    return value !== '' && value !== null && value !== undefined
  }

  const normalizeQuickValue = (field, value) => {
    const fieldConfig = getQuickFields()[field]
    if (!fieldConfig) return value ?? ''
    if (fieldConfig.type === 'input') {
      const inputValue = value == null ? '' : String(value)
      return fieldConfig.trim === false ? inputValue : inputValue.trim()
    }
    if (fieldConfig.type === 'select-multiple') {
      if (!Array.isArray(value) || !value.length) return ''
      return value.join(',')
    }
    return value ?? ''
  }

  const mergeAdvancedSnapshotWithLatestConfig = (snapshot) => {
    const latestConfig = getAdvancedConfig()
    const mergedConfig = {}

    Object.values(latestConfig).forEach(field => {
      mergedConfig[field.key] = field
    })

    ;['filter', 'order', 'other'].forEach(groupKey => {
      Object.values(snapshot?.[groupKey] || {}).forEach(field => {
        const latestField = latestConfig[field.key]
        mergedConfig[field.key] = latestField
          ? {
            ...latestField,
            ...field,
            options: latestField.options ?? field.options
          }
          : field
      })
    })

    return mergedConfig
  }

  const advancedFilterCondition = computed(() => {
    if (advancedFilterSnapshot.value) {
      return mergeAdvancedSnapshotWithLatestConfig(advancedFilterSnapshot.value)
    }
    return getAdvancedConfig()
  })

  const applyQuickSearch = () => {
    const field = quickSearchField.value
    const value = normalizeQuickValue(field, quickSearchValue.value)

    resetAdvancedSearchState()
    searchMode.value = 'quick'

    if (!hasValue(value)) {
      clearSearchObject()
      searchFun()
      return
    }

    patchSearchObject({ [field]: value })
    searchFun()
  }

  const applyAdvancedSearch = ({ filterObj, filterStr: summary, rawForm }) => {
    resetQuickSearchState()
    searchMode.value = 'advanced'
    advancedFilterSnapshot.value = rawForm
    filterStr.value = summary || ''
    patchSearchObject(filterObj)
    searchFun()
  }

  const resetAllSearch = () => {
    resetQuickSearchState()
    resetAdvancedSearchState()
    searchMode.value = 'quick'
    clearSearchObject()
    searchFun()
  }

  const handleQuickFieldChange = async(field) => {
    quickSearchValue.value = ''
    resetAdvancedSearchState()
    searchMode.value = 'quick'
    clearSearchObject()

    try {
      if (onQuickFieldChange) await onQuickFieldChange(field)
    } catch (error) {
      return error
    } finally {
      searchFun()
    }
  }

  const handleQuickValueChange = () => {
    applyQuickSearch()
  }

  const handleQuickInput = () => {
    if (quickSearchTimer.value) clearTimeout(quickSearchTimer.value)
    quickSearchTimer.value = setTimeout(() => {
      applyQuickSearch()
    }, debounce)
  }

  const openAdvancedDrawer = () => {
    advancedDrawerVisible.value = true
    if (onOpenAdvanced) Promise.resolve(onOpenAdvanced()).catch(() => undefined)
  }

  const handleAdvancedSubmit = ({ filterObj, filterStr: summary, rawForm }) => {
    applyAdvancedSearch({ filterObj, filterStr: summary, rawForm })
  }

  const handleAdvancedCancel = () => {
    advancedDrawerVisible.value = false
  }

  onBeforeUnmount(() => {
    if (quickSearchTimer.value) clearTimeout(quickSearchTimer.value)
  })

  return {
    searchMode,
    quickSearchField,
    quickSearchValue,
    advancedDrawerVisible,
    filterStr,
    advancedFilterCondition,
    resetAllSearch,
    handleQuickFieldChange,
    handleQuickValueChange,
    handleQuickInput,
    openAdvancedDrawer,
    handleAdvancedSubmit,
    handleAdvancedCancel
  }
}
